import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Sparkles, Mic, MicOff, X, Loader2, Send, Pencil, Trash2, Check, Calendar,
  ArrowRight, Lock,
} from "lucide-react";
import { endpoints } from "@/lib/api";
import { N8 } from "@/components/BrandMark";

/**
 * SmartEntryModal — Pro-only single-input tracking capture.
 *
 * Flow: enter → parse (Claude) → review → save → done.
 *
 *   1. Compose  – textarea + mic + date picker. Submit calls
 *                 /api/smart-entry/parse which returns structured items
 *                 (and, if the message was ambiguous, needs_clarify
 *                 questions) via Claude 4.5.
 *   2. Review   – confirmation grid. Each item can be edited or removed.
 *                 needs_clarify prompts render above the list.
 *   3. Save     – calls /api/smart-entry/save which routes each item
 *                 into its underlying tracker collection (same records
 *                 the individual tracker views read), skipping any
 *                 duplicates for the selected date.
 *   4. Done     – short toast + close.
 *
 * If the profile is not Pro, the modal shows a soft upgrade card
 * instead of the composer.
 */

const TITLES = {
  compose: <><N8 /> Smart Entry</>,
  review: "Here's what I found",
  saving: "Saving…",
};

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function unitLabel(item) {
  if (item.value_text) return item.value_text;
  if (item.status === "completed") return "completed";
  if (item.value != null && item.unit) return `${item.value} ${item.unit}`;
  if (item.value != null) return String(item.value);
  return "—";
}

function trackerLabel(tracker) {
  return String(tracker || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function SmartEntryModal({ open, onClose, isPro, onSaved, initialDate }) {
  const [stage, setStage] = useState("compose"); // compose | review | saving
  const [text, setText] = useState("");
  const [entryDate, setEntryDate] = useState(initialDate || todayIso());
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [clarifies, setClarifies] = useState([]);
  const [listening, setListening] = useState(false);
  const [gaps, setGaps] = useState([]);
  const recogRef = useRef(null);

  // Reset when the modal closes so re-opens start clean.
  useEffect(() => {
    if (!open) {
      setStage("compose");
      setText("");
      setBusy(false);
      setItems([]);
      setClarifies([]);
      setGaps([]);
      setListening(false);
      if (recogRef.current) {
        try { recogRef.current.stop(); } catch {} // eslint-disable-line no-empty
      }
    } else {
      // Re-sync date when opening (parent may have set initialDate to yesterday).
      setEntryDate(initialDate || todayIso());
    }
  }, [open, initialDate]);

  // Fetch daily gaps whenever the compose stage is showing and Pro is on.
  useEffect(() => {
    if (!open || !isPro || stage !== "compose") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await endpoints.smartEntryGaps(entryDate);
        if (!cancelled) setGaps(Array.isArray(data.gaps) ? data.gaps : []);
      } catch { if (!cancelled) setGaps([]); }
    })();
    return () => { cancelled = true; };
  }, [open, isPro, stage, entryDate]);

  // Web Speech API — free STT. Availability depends on the browser.
  const speechSupported = useMemo(() => {
    if (typeof window === "undefined") return false;
    return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  }, []);

  const startListening = () => {
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) {
      toast.error("Voice input is not supported on this browser.");
      return;
    }
    const rec = new Ctor();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    let final = text ? text + " " : "";
    rec.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const r = ev.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      setText((final + interim).trim());
    };
    rec.onerror = (e) => {
      // eslint-disable-next-line no-console
      console.warn("Speech recognition error", e);
      setListening(false);
    };
    rec.onend = () => setListening(false);
    rec.start();
    recogRef.current = rec;
    setListening(true);
  };

  const stopListening = () => {
    if (recogRef.current) {
      try { recogRef.current.stop(); } catch {} // eslint-disable-line no-empty
    }
    setListening(false);
  };

  const handleParse = async () => {
    if (!text.trim()) {
      toast.error("Tell N8 what you did first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await endpoints.smartEntryParse({ text: text.trim(), entry_date: entryDate });
      const gotItems = Array.isArray(data.items) ? data.items : [];
      const gotClarify = Array.isArray(data.needs_clarify) ? data.needs_clarify : [];
      if (gotItems.length === 0 && gotClarify.length === 0) {
        toast("N8 couldn't extract anything. Try adding a specific quantity or activity.");
        return;
      }
      setItems(gotItems);
      setClarifies(gotClarify);
      setStage("review");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("N8 Smart Entry is a Pro feature.");
      } else {
        toast.error("N8 couldn't parse that. Try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!items.length) {
      toast("Nothing to save.");
      return;
    }
    setStage("saving");
    try {
      const { data } = await endpoints.smartEntrySave({
        items: items.map((it) => ({ ...it, entry_date: it.entry_date || entryDate })),
        entry_date: entryDate,
      });
      const saved = data.saved || 0;
      const skipped = data.skipped || 0;
      if (saved && skipped) {
        toast.success(`${saved} saved · ${skipped} already logged today.`);
      } else if (skipped && !saved) {
        toast(`${skipped} already logged today.`);
      } else {
        toast.success(`${saved} tracked and organized.`);
      }
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("N8 Smart Entry is a Pro feature.");
      } else {
        toast.error("Could not save. Try again.");
      }
      setStage("review");
    }
  };

  const updateItem = (idx, patch) => {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => {
    setItems((cur) => cur.filter((_, i) => i !== idx));
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-start md:items-center justify-center p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="N8 Smart Entry"
      data-testid="smart-entry-modal"
    >
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[3px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative z-10 w-full max-w-2xl rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-8 pt-6 pb-4 border-b border-[hsl(var(--border))]/60">
          <div className="flex items-center gap-3">
            <div className="grid place-items-center w-9 h-9 rounded-2xl bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--terracotta))]">
              <Sparkles size={18} />
            </div>
            <div>
              <div className="font-display text-xl leading-tight">{TITLES[stage] || TITLES.compose}</div>
              {stage === "compose" && (
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  Tell <N8 /> what you did today. We'll organize the rest.
                </div>
              )}
              {stage === "review" && (
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  Review, edit, or remove — then save all at once.
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="smart-entry-close"
            className="rounded-full p-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--muted))]/60 tab-link"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 md:px-8 py-6 max-h-[70vh] overflow-y-auto">
          {!isPro && (
            <ProUpgradeCard onClose={onClose} />
          )}

          {isPro && stage === "compose" && (
            <ComposeStage
              text={text}
              setText={setText}
              entryDate={entryDate}
              setEntryDate={setEntryDate}
              busy={busy}
              onParse={handleParse}
              listening={listening}
              startListening={startListening}
              stopListening={stopListening}
              speechSupported={speechSupported}
              gaps={gaps}
              onNudge={(gap) => {
                // Tapping a nudge chip drops a friendly line into the
                // textarea so the user can flesh it out with a number.
                const seeds = {
                  hydration: "I drank ",
                  movement: "I moved for ",
                  meditation: "I meditated for ",
                  sleep: "I slept ",
                  skincare: "I did my skincare",
                  supplements: "I took ",
                };
                const seed = seeds[gap.key] || "";
                setText((cur) => {
                  const sep = cur && !cur.endsWith(" ") ? " " : "";
                  return `${cur}${sep}${seed}`;
                });
              }}
            />
          )}

          {isPro && stage === "review" && (
            <ReviewStage
              items={items}
              clarifies={clarifies}
              onUpdate={updateItem}
              onRemove={removeItem}
              onBack={() => setStage("compose")}
              onSave={handleSave}
              onDismissClarify={(i) => setClarifies((cur) => cur.filter((_, j) => j !== i))}
            />
          )}

          {isPro && stage === "saving" && (
            <div className="flex items-center justify-center gap-3 py-16 text-[hsl(var(--muted-foreground))]">
              <Loader2 size={18} className="animate-spin" />
              <span>Organizing your entry…</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------

function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function ComposeStage({
  text, setText, entryDate, setEntryDate, busy,
  onParse, listening, startListening, stopListening, speechSupported,
  gaps = [], onNudge,
}) {
  const today = todayIso();
  const yesterday = yesterdayIso();
  return (
    <div className="space-y-5">
      {gaps.length > 0 && (
        <div data-testid="smart-entry-nudges">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] mb-2">
            Quiet nudges from <N8 />
          </div>
          <div className="flex flex-wrap gap-2">
            {gaps.map((g, i) => (
              <button
                key={g.key}
                type="button"
                onClick={() => onNudge?.(g)}
                data-testid={`smart-entry-nudge-${g.key}`}
                className="text-left rounded-full border border-[hsl(var(--terracotta))]/25 bg-[hsl(var(--terracotta))]/6 text-[13px] px-3 py-1.5 hover:bg-[hsl(var(--terracotta))]/12 tab-link"
              >
                {g.prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="block">
        <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
          What did you do today?
        </span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder="Today I walked 8,000 steps, drank 72 oz of water, did 20 minutes of strength training, took collagen and NAD, used red light for 15 minutes…"
          className="mt-2 w-full rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-3 text-[15px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-[hsl(var(--terracotta))]/50 focus:border-[hsl(var(--terracotta))]/60 resize-none"
          data-testid="smart-entry-text"
          autoFocus
        />
      </label>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
            <Calendar size={14} />
            <span>Date</span>
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm"
              data-testid="smart-entry-date"
            />
          </label>
          <button
            type="button"
            onClick={() => setEntryDate(today)}
            data-testid="smart-entry-date-today"
            className={`text-xs rounded-full px-2.5 py-1 border tab-link ${
              entryDate === today
                ? "bg-[hsl(var(--fg))] text-[hsl(var(--bg))] border-transparent"
                : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
            }`}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setEntryDate(yesterday)}
            data-testid="smart-entry-date-yesterday"
            className={`text-xs rounded-full px-2.5 py-1 border tab-link ${
              entryDate === yesterday
                ? "bg-[hsl(var(--fg))] text-[hsl(var(--bg))] border-transparent"
                : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
            }`}
          >
            Yesterday
          </button>
        </div>

        <div className="flex items-center gap-2">
          {speechSupported && (
            <button
              type="button"
              onClick={listening ? stopListening : startListening}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm border tab-link ${
                listening
                  ? "border-[hsl(var(--terracotta))] text-[hsl(var(--terracotta))] bg-[hsl(var(--terracotta))]/10"
                  : "border-[hsl(var(--border))] text-[hsl(var(--fg))] hover:bg-[hsl(var(--muted))]/50"
              }`}
              data-testid="smart-entry-mic"
              aria-pressed={listening}
              aria-label={listening ? "Stop voice input" : "Start voice input"}
            >
              {listening ? <MicOff size={14} /> : <Mic size={14} />}
              <span>{listening ? "Listening…" : "Voice"}</span>
            </button>
          )}
          <button
            type="button"
            onClick={onParse}
            disabled={busy || !text.trim()}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium bg-[hsl(var(--terracotta))] text-white hover:bg-[hsl(var(--terracotta))]/90 disabled:opacity-50 tab-link"
            data-testid="smart-entry-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            <span>{busy ? "Reading…" : "Organize this"}</span>
          </button>
        </div>
      </div>

      {!speechSupported && (
        <div className="text-xs text-[hsl(var(--muted-foreground))]">
          Voice input isn't available on this browser. You can still type freely.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ReviewStage({
  items, clarifies, onUpdate, onRemove, onBack, onSave, onDismissClarify,
}) {
  return (
    <div className="space-y-5">
      {clarifies.length > 0 && (
        <div className="space-y-2">
          {clarifies.map((c, i) => (
            <div
              key={i}
              className="rounded-2xl border border-[hsl(var(--terracotta))]/30 bg-[hsl(var(--terracotta))]/6 px-4 py-3 text-sm"
              data-testid={`smart-entry-clarify-${i}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{c.question}</div>
                  {Array.isArray(c.choices) && c.choices.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                      {c.choices.map((choice, j) => (
                        <span key={j} className="rounded-full border border-[hsl(var(--border))] px-2 py-0.5">
                          {choice}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onDismissClarify(i)}
                  className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
                >
                  Skip
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && (
        <div className="text-sm text-[hsl(var(--muted-foreground))] italic">
          Nothing to save. Head back and add a specific activity.
        </div>
      )}

      <ul className="space-y-2" data-testid="smart-entry-items">
        {items.map((it, idx) => (
          <ReviewRow
            key={idx}
            item={it}
            testid={`smart-entry-item-${idx}`}
            onChange={(patch) => onUpdate(idx, patch)}
            onRemove={() => onRemove(idx)}
          />
        ))}
      </ul>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
          data-testid="smart-entry-back"
        >
          ← Edit entry
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={items.length === 0}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium bg-[hsl(var(--terracotta))] text-white hover:bg-[hsl(var(--terracotta))]/90 disabled:opacity-50 tab-link"
          data-testid="smart-entry-save-all"
        >
          <Check size={14} />
          <span>Save all</span>
        </button>
      </div>
    </div>
  );
}

function ReviewRow({ item, onChange, onRemove, testid }) {
  const [editing, setEditing] = useState(false);
  return (
    <li
      className="rounded-2xl border border-[hsl(var(--border))] px-4 py-3 flex items-start justify-between gap-3 bg-[hsl(var(--background))]"
      data-testid={testid}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
          {item.category}
        </div>
        <div className="font-medium mt-0.5 truncate">{trackerLabel(item.tracker)}</div>
        {!editing ? (
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
            {unitLabel(item)}
            {item.note ? ` · ${item.note}` : ""}
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <input
              type="number"
              value={item.value ?? ""}
              onChange={(e) => onChange({ value: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="value"
              className="w-24 rounded-md border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm"
              data-testid={`${testid}-value`}
            />
            <input
              type="text"
              value={item.unit || ""}
              onChange={(e) => onChange({ unit: e.target.value })}
              placeholder="unit"
              className="w-20 rounded-md border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm"
              data-testid={`${testid}-unit`}
            />
            <input
              type="text"
              value={item.value_text || ""}
              onChange={(e) => onChange({ value_text: e.target.value })}
              placeholder="label"
              className="flex-1 min-w-[8rem] rounded-md border border-[hsl(var(--border))] bg-transparent px-2 py-1 text-sm"
              data-testid={`${testid}-text`}
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          aria-label={editing ? "Done editing" : "Edit item"}
          className="rounded-full p-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--muted))]/50 tab-link"
          data-testid={`${testid}-edit`}
        >
          {editing ? <Check size={14} /> : <Pencil size={14} />}
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove item"
          className="rounded-full p-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))] hover:bg-[hsl(var(--muted))]/50 tab-link"
          data-testid={`${testid}-remove`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------

function ProUpgradeCard({ onClose }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--terracotta))]/25 bg-[hsl(var(--terracotta))]/6 p-6 space-y-4" data-testid="smart-entry-upgrade">
      <div className="flex items-center gap-2 text-[hsl(var(--terracotta))]">
        <Lock size={16} />
        <span className="text-xs uppercase tracking-[0.24em]"><N8 /> Pro</span>
      </div>
      <div>
        <div className="font-display text-2xl leading-tight">Track more. Type less.</div>
        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-md">
          With <N8 /> Pro, tell <N8 /> everything you did in one place and let it organize your tracking automatically —
          across movement, hydration, supplements, treatments, and skincare.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <a
          href="/profile"
          onClick={onClose}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-[hsl(var(--terracotta))] text-white hover:bg-[hsl(var(--terracotta))]/90 tab-link"
          data-testid="smart-entry-upgrade-cta"
        >
          <span>Go Deeper with <N8 /> Pro</span>
          <ArrowRight size={14} />
        </a>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
